import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UndecoratedProfilePreference } from '@flemingmetrics/api-client';
import { setCurrentUser } from './currentUserSlice';
import { mergeWithoutDuplicates } from '@/utils/arrays';
import { buildCompositeKey } from '@/store/utils';

type ProfilePreferencesState = {
  byId: Record<string, UndecoratedProfilePreference>;
  byProfileId: Record<string, string[]>;
};

const initialState: ProfilePreferencesState = {
  byId: {},
  byProfileId: {},
};

export const profilePreferencesSlice = createSlice({
  name: 'profilePreferences',
  initialState,
  reducers: {
    addProfilePreference: (
      state,
      action: PayloadAction<UndecoratedProfilePreference>
    ) => {
      return getNextState(state, [action.payload]);
    },
    setProfilePreferences: (
      state,
      action: PayloadAction<UndecoratedProfilePreference[]>
    ) => {
      return getNextState(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCurrentUser, (state, action) => {
        return getNextState(state, action.payload.profilePreferences);
      })
      .addDefaultCase(() => {});
  },
});

function getNextState(
  state: ProfilePreferencesState,
  profilePreferences: UndecoratedProfilePreference[]
) {
  const byId = { ...state.byId };
  const byProfileId = { ...state.byProfileId };
  profilePreferences.forEach((pp) => {
    const key = buildProfilePreferenceKey(pp.profileId, pp.preferenceId);
    byId[key] = pp;
    if (!byProfileId[pp.profileId]) {
      byProfileId[pp.profileId] = [];
    }
    byProfileId[pp.profileId] = mergeWithoutDuplicates(
      byProfileId[pp.profileId],
      [key]
    );
  });
  return { byId, byProfileId };
}

export function buildProfilePreferenceKey(
  profileId: number,
  preferenceId: number
) {
  return buildCompositeKey(profileId, preferenceId);
}

export const { addProfilePreference, setProfilePreferences } =
  profilePreferencesSlice.actions;

export default profilePreferencesSlice.reducer;
